import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { Logo } from './Logo';
import { NAV_LINKS } from '../data/content';
import { scrollToId, scrollToTop } from '../lib/scroll';

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 200 : 0);
  };

  const linkColor = scrolled ? 'text-coffee hover:text-bronze' : 'text-cream/85 hover:text-gold';

  return (
    <>
      <header
        data-testid="nav-container"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? 'border-b border-bronze/20 bg-cream/85 backdrop-blur-xl' : 'bg-gradient-to-b from-espresso/60 to-transparent'
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <button data-testid="nav-logo-link" onClick={scrollToTop} aria-label="Invicto Homes — back to top">
            <Logo variant={scrolled ? 'dark' : 'light'} />
          </button>

          <div className="hidden items-center gap-9 md:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}-link`}
                onClick={() => go(l.id)}
                className={`group relative font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${linkColor}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            <button
              data-testid="nav-cta-button"
              onClick={() => go('contact')}
              className="group flex items-center gap-2 bg-gold px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-goldhover"
            >
              Start Your Build
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className={`md:hidden ${scrolled ? 'text-espresso' : 'text-cream'}`}
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-espresso/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => go(l.id)}
                  className="border-b border-gold/15 py-5 text-left font-serif text-4xl font-light text-cream"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => go('contact')}
                className="mt-8 border border-gold bg-gold px-6 py-4 font-mono text-xs uppercase tracking-[0.3em] text-espresso"
              >
                Start Your Build
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
